import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import styles from "./UPSTab.module.css";
import ProductHeader from "../ProductHeader/ProductHeader";
import FeaturesList from "../FeaturesList/FeaturesList";
import PreviewItem from "../PreviewItem/PreviewItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Icons from "@fortawesome/free-solid-svg-icons";
import { CSSTransition } from "react-transition-group";
import { StaticRoutes } from "../../pages/routes";

const UPSTab = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const collapseRef = useRef(null);

  const toggleCollapse = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const children = (
    <>
      Already a UPS pilot with a BidPro account?
      <br />
      Click <Link to="https://bidding.bidproplus.com/">here</Link> to go to the
      Schedule Bidder, or download the iPad app on the{" "}
      <Link to="https://itunes.apple.com/us/app/bidpro-plus-ipa/id1251050627?mt=8">
        App Store
      </Link>
      .
    </>
  );

  const features = [
    {
      icon: "fa-check",
      header: "Line Hold Status",
      description:
        "View the hold status of each line to get an idea of which lines you may be awarded – before even submitting your bids.",
    },
    {
      icon: "fa-rotate",
      header: "Device Syncing",
      description:
        "Work across multiple devices and platforms without losing your work by syncing your devices.",
    },
    {
      icon: "fa-floppy-disk",
      header: "Back Up & Restore Data",
      description:
        "Create unlimited backups of your work, and restore them any time.",
    },
    {
      icon: "fa-print",
      header: "Printing",
      description:
        "Easily print a physical copy of details about individual pairings, or all trips in a line.",
    },
    {
      icon: "fa-users",
      header: "Multiple Profiles",
      description:
        "Create unlimited profiles to view bid packages for other fleets, seats and domiciles.",
    },
    {
      icon: "fa-bell",
      header: "Notifications",
      description:
        "Receive push notifications when new bid packages become available.",
    },
    {
      icon: "fa-cogs",
      header: "Customization",
      description:
        "Make the app your own by customizing your colors, layout, and list names.",
    },
    {
      icon: "fa-download",
      header: "Updates",
      description:
        "Free updates are frequently released to the App Store with new features and bug fixes.",
    },
    {
      icon: "fa-circle-question",
      header: "Support",
      description:
        "Contact BidPro Tech Support, view tutorials and read FAQs all within the app.",
    },
  ];

  const sortLinesChildren = (
    <>
      <small className={styles.small}>
        Lists can be sorted by pay, days off, total credit, and{" "}
        <a
          href="#UPSSorts"
          onClick={toggleCollapse}
          role="button"
          aria-expanded="false"
        >
          more {isOpen ? "▲" : "▼"}{" "}
        </a>
      </small>
      <CSSTransition
        in={isOpen}
        timeout={300}
        classNames={{
          enter: styles.slideEnter,
          enterActive: styles.slideEnterActive,
          exit: styles.slideExit,
          exitActive: styles.slideExitActive,
        }}
        unmountOnExit
        nodeRef={collapseRef}
      >
        <div
          ref={collapseRef}
          className={`collapse ${isOpen ? "show" : ""} ${styles.collapse}`}
          id="UPSSorts"
        >
          <div className="card card-body">
            <h6>Available sort criteria for UPS pilots:</h6>
            <ul className={`ul ${styles.sortOptions}`}>
              <li>Pay</li>
              <li>Total credit</li>
              <li>Number of trips</li>
              <li>Number of days off</li>
              <li>Total duty time</li>
              <li>Number of commutes</li>
              <li>Specific dates off</li>
              <li>Average layover time</li>
              <li>Percentage of Saturdays off</li>
              <li>Percentage of Sundays off</li>
              <li>Percentage of full weekends off</li>
              <li>Percentage of FOL at specific airport</li>
              <li>Percentage of LOL at specific airport</li>
              <li>Percentage of layovers at specific airport</li>
              <li>Commercial to work/home</li>
              <li>Carry-out credit</li>
              <li>Base trip line</li>
              <li>EDW percentage of duty periods</li>
              <li>International percentage of duty periods</li>
              <li>Ratio of block time to total pay</li>
              <li>Average duty time</li>
              <li>Average ground time</li>
              <li>Average landings per duty period</li>
              <li>Ratio of block time to time away from base</li>
              <li>Number of duty days</li>
              <li>Ratio of duty time to time away from base</li>
              <li>Ratio of duty time to total pay</li>
              <li>Number of landings</li>
              <li>Time away from base</li>
              <li>Total block</li>
            </ul>
          </div>
        </div>
      </CSSTransition>
    </>
  );

  const previewItemFooter = (
    <>
      Want to learn more?{" "}
      <Link to="https://airlinedata.blob.core.windows.net/ups/ipad-tutorial.pdf">
        View the UPS Tutorial{" "}
        <FontAwesomeIcon
          className={styles.faIcon}
          icon={Icons.faArrowUpRightFromSquare}
        />
      </Link>
    </>
  );

  const onClick = () => {
    navigate(StaticRoutes.UPSPricing);
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.headerRow}>
        <ProductHeader
          title={"UPS Airlines"}
          description={
            "BidPro has been working with the Independent Pilots Association (IPA) since 2016 to provide advanced web-based and iPad-based Bidding applications to UPS pilots."
          }
          img={"UPS-bidding-view.png"}
          children={children}
        />
      </div>
      <div className={styles.row}>
        <FeaturesList features={features} colorBlue={true} />
      </div>
      <div className={styles.row}>
        <PreviewItem
          imgRight={true}
          header={"View Bid Data"}
          description={
            "Bid data is easily viewed in 3 different layouts – the Calendar view, Bid Packet view and Browser view."
          }
          imgs={[
            {
              src: "UPS-bidding-view.png",
              alt: "Image of UPS BidPro Plus! Bidding on laptop and iPad screens.",
            },
          ]}
        />
      </div>
      <div className={styles.row}>
        <PreviewItem
          imgRight={false}
          header={"Organize Lines into Lists"}
          description={
            "See some bid lines that you want to eliminate, or that you want to keep an eye on? Organize your bids by separating them into four different lists."
          }
          imgs={[
            {
              src: "UPS-bidding-lists.jpg",
              alt: "Image of moving line on BidPro Plus! Bidding iPad app",
            },
          ]}
        />
      </div>
      <div className={styles.row}>
        <PreviewItem
          imgRight={true}
          header={"Sort Lines"}
          description={
            "Sorting allows you to quickly organize your lines in a way that works best for you. Each list is sorted individually without affecting your other lists."
          }
          imgs={[
            {
              src: "UPS-bidding-sorts.png",
              alt: "Image of BidPro Plus! Bidding iPad app sort criteria",
            },
          ]}
          children={sortLinesChildren}
        />
      </div>
      <div className={styles.row}>
        <PreviewItem
          imgRight={false}
          header={"Submit Bids to UPS"}
          description={
            "In just a couple of taps, you can copy the line numbers from the list you'd like to submit, and then submit your bids to UPS as normal."
          }
          imgs={[
            {
              src: "UPS-bidding-submit.png",
              alt: "Image of submitting bids on BidPro Plus! Bidding iPad app",
            },
          ]}
          footer={previewItemFooter}
        />
      </div>
      <div className={styles.contactRow}>
        <h2 className={styles.h2}>Ready to get started?</h2>
      </div>
      <div className={styles.contactRow}>
        <button
          type="submit"
          className="btn btn-rounded btn-primary btn-lg"
          id={styles.button}
          onClick={onClick}
        >
          SIGN UP
        </button>
      </div>
    </div>
  );
};

export default UPSTab;
